import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCart } from '../../context/CartContext';
import { detailedProducts } from '../../data/products';
import './ProductPage.css';

const ZOOM_LEVEL = 2.5;

const ProductPage = ({ productId, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [selectedPricing, setSelectedPricing] = useState(0);
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  const imageContainerRef = useRef(null);
  const { addToCart } = useCart();

  const product = detailedProducts[productId];

  useEffect(() => {
    if (product) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, [product]);

  useEffect(() => {
    setCurrentImageIndex(0);
    setZoom({ active: false, x: 50, y: 50 });
  }, [selectedVariant]);

  const isTouchDevice = useRef(false);

  useEffect(() => {
    isTouchDevice.current = window.matchMedia('(hover: none)').matches || 'ontouchstart' in window;
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (isTouchDevice.current) return;
    const rect = imageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ active: true, x, y });
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isTouchDevice.current) return;
    setZoom({ active: false, x: 50, y: 50 });
  }, []);

  const handleTouchStart = useCallback(() => {
    isTouchDevice.current = true;
    setZoom({ active: false, x: 50, y: 50 });
  }, []);

  if (!product) return null;

  const currentVariant = product.variants[selectedVariant];
  const currentImages = currentVariant.images;

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === currentImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? currentImages.length - 1 : prev - 1
    );
  };

  const handleAddToCart = () => {
    addToCart({
      id: `${product.id}-${selectedVariant}-${selectedPricing}`,
      name: `${product.name} - ${currentVariant.name}`,
      image: currentImages[0],
      category: product.category,
      variant: currentVariant.name,
      pieces: product.pricing[selectedPricing].pieces,
      price: product.pricing[selectedPricing].price,
    });
  };

  return (
    <>
      <div className="product-page-overlay" onClick={onClose}></div>
      <div className="product-page">
        <div className="product-page-content">
          <button className="close-product-page" onClick={onClose}>×</button>

          <div className="product-gallery">
            {/* Main zoomable image */}
            <div
              ref={imageContainerRef}
              className={`main-image-container${zoom.active ? ' zoomed' : ''}`}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
            >
              <img
                src={currentImages[currentImageIndex]}
                alt={`${product.name} - ${currentVariant.name}`}
                className="main-product-image"
                style={
                  zoom.active
                    ? {
                        transformOrigin: `${zoom.x}% ${zoom.y}%`,
                        transform: `scale(${ZOOM_LEVEL})`,
                      }
                    : {}
                }
                loading="lazy"
                draggable={false}
              />
              {currentImages.length > 1 && (
                <>
                  <button
                    className="gallery-nav prev"
                    onClick={prevImage}
                    onMouseEnter={handleMouseLeave}
                  >‹</button>
                  <button
                    className="gallery-nav next"
                    onClick={nextImage}
                    onMouseEnter={handleMouseLeave}
                  >›</button>
                </>
              )}
              {!zoom.active && (
                <span className="zoom-hint">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7"/>
                    <line x1="16.5" y1="16.5" x2="22" y2="22"/>
                  </svg>
                </span>
              )}
            </div>

            {currentImages.length > 1 && (
              <div className="image-thumbnails">
                {currentImages.map((image, index) => (
                  <button
                    key={index}
                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img src={image} alt={`View ${index + 1}`} loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="product-details">
            <div className="product-header">
              <h1>{product.name}</h1>
              <p className="product-category">{product.category}</p>
            </div>

            <div className="product-description">
              <p>{product.description}</p>
              <div className="long-description">
                <p>{product.longDescription}</p>
              </div>
            </div>

            <div className="product-options">
              <div className="variant-selection">
                <h3>Color Options</h3>
                <div className="variant-buttons">
                  {product.variants.map((variant, index) => (
                    <button
                      key={variant.id}
                      className={`variant-btn ${index === selectedVariant ? 'active' : ''}`}
                      onClick={() => setSelectedVariant(index)}
                    >
                      {variant.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pricing-selection">
                <h3>Set Options</h3>
                <div className="pricing-options">
                  {product.pricing.map((option, index) => (
                    <label key={index} className="pricing-option">
                      <input
                        type="radio"
                        name="pricing"
                        value={index}
                        checked={selectedPricing === index}
                        onChange={() => setSelectedPricing(index)}
                      />
                      <div className="pricing-details">
                        <span className="pieces">{option.pieces} Pieces</span>
                        <span className="price">${option.price}</span>
                        <span className="description">{option.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="product-actions">
              <button className="add-to-cart-btn" onClick={handleAddToCart}>
                Add to Cart - ${product.pricing[selectedPricing].price}
              </button>
              <p className="cart-note">
                Items will be added to your inquiry list for personalized assistance
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductPage;